import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { apiClientRegistryScope } from '@/app/core/current-user/api-client-registry-scope';
import { CurrentUserService } from '@/app/core/current-user/current-user.service';
import { ScopeService } from '@/app/core/scope/scope.service';

/**
 * The /api-klijenti route guard (UX pregled 4, D-UX4-1): `apiClientRegistryScope` decides, from the flags, never from a refused
 * read —
 * - the registry for an operator holding the GLOBAL API-client, lease and Tenants flags (the whole cross-tenant read, N8);
 * - a scope bounded to ONE tenant whose hub tab „API klijenti“ opens: that tab (`/tenanti/:id/api-klijenti`) — the capability of
 *   that tenant is loaded first (`ensureTenantCapability`), an unknown one reads „no“. The refused URL is never committed (the
 *   router's deferred URL update), so Back does not return to /api-klijenti — the `/lease` rule;
 * - anyone else: `/zabranjen-pristup`.
 *
 * Kept apart from the component so the route table can read it without pulling the lazily loaded screen into the main bundle.
 */
export const apiClientRegistryGuard: CanActivateFn = async () => {
    // inject() only works synchronously, before the first await.
    const currentUser = inject(CurrentUserService);
    const scope = inject(ScopeService);
    const router = inject(Router);
    const denied = router.createUrlTree(['/zabranjen-pristup']);
    try {
        await currentUser.ensureLoaded();
    } catch {
        return denied;
    }
    // The shell's `currentUserGuard` has derived it already; deriving again for the same actor changes nothing.
    scope.initializeFromActor();
    const current = scope.currentScope();
    if (current.kind === 'bounded' && current.tenantIds.length === 1) await currentUser.ensureTenantCapability(current.tenantIds[0]);
    const decided = apiClientRegistryScope(currentUser, scope.currentScope());
    switch (decided.kind) {
        case 'registry':
            return true;
        case 'redirect':
            return router.createUrlTree(['/tenanti', decided.tenantId, 'api-klijenti']);
        case 'forbidden':
            return denied;
    }
};
